import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import WizardSelect from '../../../components/wizard/WizardSelect';
import { useAttorneyGeneralWizardContext } from '../../../hooks/useCaseRequestWizard';
import { contactNumberError } from '../../../utils/caseFieldValidation';
import { useWizardMaxLengthChange } from '../useWizardMaxLengthChange';

import { focusFirstInvalid, type RequiredErrors } from '../../../utils/requiredValidation';
import { SectionCard } from '../WizardComponents';
import { AttorneyGeneralWizardActions } from './AttorneyGeneralWizardActions';
import { attorneyGeneralWizardPath, AttorneyGeneralWizardShell, FieldError, inpClass } from './AttorneyGeneralWizardShell';
import { preferredAccessLabels } from './attorneyGeneralHelpers';
import { translatePreferredAccessName } from '../rightToInformation/rightToInformationHelpers';
import { agEnterPlaceholder, agSelectPlaceholder } from './attorneyGeneralPlaceholders';
import { validateAttorneyGeneralStep } from './attorneyGeneralStepValidation';
import { useAttorneyGeneralLookups } from './useAttorneyGeneralLookups';
import { useWizardFieldLabels } from '../useWizardFieldLabels';

const MAX_NAME = 2000;
const MAX_SHORT = 255;
const MAX_CONTACT = 10;

export default function AttorneyGeneralStep9RightToInformationPage() {
  const { t } = useTranslation();
  const f = useWizardFieldLabels();
  const nav = useNavigate();
  const { draft, setDraft } = useAttorneyGeneralWizardContext();
  const [errors, setErrors] = useState<RequiredErrors>({});
  const { preferredMethodOfAccess, preferredLanguages } = useAttorneyGeneralLookups('step9');

  const clearErr = (key: string) => setErrors((e) => ({ ...e, [key]: '' }));
  const maxLen = useWizardMaxLengthChange(setErrors);

  const validate = (): boolean => {
    const { valid, errors: nextErrors, specs } = validateAttorneyGeneralStep(9, draft);
    setErrors(nextErrors);
    if (!valid) focusFirstInvalid(specs, nextErrors);
    return valid;
  };

  const togglePreferredAccess = (id: string) => {
    setDraft((d) => {
      const has = d.rtiPreferredMethodOfAccessIds.includes(id);
      const ids = has ? d.rtiPreferredMethodOfAccessIds.filter((x) => x !== id) : [...d.rtiPreferredMethodOfAccessIds, id];
      const names = preferredAccessLabels(ids, preferredMethodOfAccess);
      return { ...d, rtiPreferredMethodOfAccessIds: ids, rtiPreferredMethodOfAccessNames: names };
    });
    clearErr('rtiPreferredMethodOfAccessIds');
  };

  return (
    <AttorneyGeneralWizardShell currentStep={9} draft={draft}>
      <SectionCard title={f.sectionRequesterDetails}>
        <div className="ccr-fr c2" style={{ marginBottom: 14 }}>
          <div className="ccr-fg">
            <label>{f.requestorName}</label>
            <input
              id="ag-rti-requestorName"
              className={inpClass(Boolean(errors.rtiRequestorName))}
              type="text"
              value={draft.rtiRequestorName}
              onChange={(e) => {
                setDraft((d) => ({ ...d, rtiRequestorName: maxLen('rtiRequestorName', e.target.value, MAX_NAME) }));
              }}
              placeholder={agEnterPlaceholder('Name of Requestor')}
            />
            <FieldError message={errors.rtiRequestorName} />
          </div>
          <div className="ccr-fg">
            <label>{f.nicPassportNumber}</label>
            <input
              id="ag-rti-nicOrPassport"
              className={inpClass(Boolean(errors.rtiNicOrPassport))}
              type="text"
              value={draft.rtiNicOrPassport}
              onChange={(e) => {
                setDraft((d) => ({ ...d, rtiNicOrPassport: maxLen('rtiNicOrPassport', e.target.value, MAX_SHORT) }));
              }}
              placeholder={agEnterPlaceholder('NIC/ Passport Number')}
            />
            <FieldError message={errors.rtiNicOrPassport} />
          </div>
        </div>

        <div className="ccr-fg" style={{ marginBottom: 14 }}>
          <label>{f.address}</label>
          <textarea
            id="ag-rti-address"
            className={inpClass(Boolean(errors.rtiAddress))}
            rows={3}
            value={draft.rtiAddress}
            onChange={(e) => {
              setDraft((d) => ({ ...d, rtiAddress: maxLen('rtiAddress', e.target.value, MAX_NAME) }));
            }}
            placeholder={agEnterPlaceholder('Address')}
          />
          <FieldError message={errors.rtiAddress} />
        </div>

        <div className="ccr-fr c2" style={{ marginBottom: 14 }}>
          <div className="ccr-fg">
            <label>{f.contactNumber}</label>
            <input
              id="ag-rti-contactNumber"
              className={inpClass(Boolean(errors.rtiContactNumber))}
              type="text"
              inputMode="numeric"
              value={draft.rtiContactNumber}
              onChange={(e) => {
                const digits = e.target.value.replace(/\D/g, '').slice(0, MAX_CONTACT);
                setDraft((d) => ({ ...d, rtiContactNumber: digits }));
                const err = contactNumberError(digits);
                setErrors((prev) => ({ ...prev, rtiContactNumber: err ?? '' }));
              }}
              placeholder={agEnterPlaceholder('Contact Number')}
            />
            <FieldError message={errors.rtiContactNumber} />
          </div>
          <div className="ccr-fg">
            <label>{f.emailAddress}</label>
            <input
              id="ag-rti-emailAddress"
              className={inpClass(Boolean(errors.rtiEmailAddress))}
              type="email"
              value={draft.rtiEmailAddress}
              onChange={(e) => {
                setDraft((d) => ({ ...d, rtiEmailAddress: maxLen('rtiEmailAddress', e.target.value, MAX_SHORT) }));
              }}
              placeholder={agEnterPlaceholder('Email Address')}
            />
            <FieldError message={errors.rtiEmailAddress} />
          </div>
        </div>

        <div className="ccr-fg" id="ag-rti-citizen" style={{ marginBottom: 0 }}>
          <label>{f.isSriLankanCitizen}</label>
          <div className="ccr-radio-row">
            <label className="ccr-radio">
              <input
                type="radio"
                name="agRtiCitizen"
                checked={draft.rtiIsSriLankanCitizen === 'yes'}
                onChange={() => setDraft((d) => ({ ...d, rtiIsSriLankanCitizen: 'yes' }))}
              />
              <span>Yes</span>
            </label>
            <label className="ccr-radio">
              <input
                type="radio"
                name="agRtiCitizen"
                checked={draft.rtiIsSriLankanCitizen === 'no'}
                onChange={() => setDraft((d) => ({ ...d, rtiIsSriLankanCitizen: 'no' }))}
              />
              <span>No</span>
            </label>
          </div>
          <FieldError message={errors.rtiIsSriLankanCitizen} />
        </div>
      </SectionCard>

      <SectionCard title={f.sectionInformationRequestDetails}>
        <div className="ccr-fg" style={{ marginBottom: 14 }}>
          <label>{f.specificPublicAuthorityName}</label>
          <input
            id="ag-rti-publicAuthority"
            className={inpClass(Boolean(errors.rtiSpecificPublicAuthorityName))}
            type="text"
            value={draft.rtiSpecificPublicAuthorityName}
            onChange={(e) => {
              setDraft((d) => ({ ...d, rtiSpecificPublicAuthorityName: maxLen('rtiSpecificPublicAuthorityName', e.target.value, MAX_SHORT) }));
            }}
            placeholder={agEnterPlaceholder('Specific Public Authority Name')}
          />
          <FieldError message={errors.rtiSpecificPublicAuthorityName} />
        </div>

        <div className="ccr-fg" style={{ marginBottom: 14 }}>
          <label>{f.requestedInformationDetails}</label>
          <textarea
            id="ag-rti-requestedDetails"
            className={inpClass(Boolean(errors.rtiRequestedInformationDetails))}
            rows={4}
            value={draft.rtiRequestedInformationDetails}
            onChange={(e) => {
              setDraft((d) => ({ ...d, rtiRequestedInformationDetails: maxLen('rtiRequestedInformationDetails', e.target.value, MAX_NAME) }));
            }}
            placeholder={agEnterPlaceholder('Requested Information Details')}
          />
          <FieldError message={errors.rtiRequestedInformationDetails} />
        </div>

        <div className="ccr-fg" style={{ marginBottom: 0 }}>
          <label>{f.informationRequestedTimePeriod}</label>
          <input
            className={inpClass(Boolean(errors.rtiInformationRequestedTimePeriod))}
            type="text"
            value={draft.rtiInformationRequestedTimePeriod}
            onChange={(e) =>
              setDraft((d) => ({
                ...d,
                rtiInformationRequestedTimePeriod: maxLen('rtiInformationRequestedTimePeriod', e.target.value, MAX_SHORT) }))
            }
            placeholder={f.informationRequestedTimePeriod}
          />
          <FieldError message={errors.rtiInformationRequestedTimePeriod} />
        </div>
      </SectionCard>

      <SectionCard title={f.sectionAdditionalInformation}>
        <div id="ag-rti-preferredAccess" style={{ marginBottom: 14 }}>
          <label style={{ fontSize: 13, fontWeight: 700, display: 'block', marginBottom: 10 }}>
            {f.preferredMethodOfAccess}
          </label>
          <div className="ccr-check-grid">
            {preferredMethodOfAccess.map((m) => (
              <label key={m.id} className="ccr-check">
                <input
                  type="checkbox"
                  checked={draft.rtiPreferredMethodOfAccessIds.includes(m.id)}
                  onChange={() => togglePreferredAccess(m.id)}
                />
                <span>{translatePreferredAccessName(m.name, t)}</span>
              </label>
            ))}
          </div>
          <FieldError message={errors.rtiPreferredMethodOfAccessIds} />
        </div>

        <div className="ccr-fr c2" style={{ marginBottom: 14 }}>
          <div className="ccr-fg">
            <label>{f.preferredLanguageForResponse}</label>
            <WizardSelect
              id="ag-rti-preferredLanguage"
              hasError={Boolean(errors.rtiPreferredLanguageId)}
              value={draft.rtiPreferredLanguageId}
              placeholder={agSelectPlaceholder('Preferred Language for Response')}
              options={preferredLanguages.map((l) => ({ id: l.id, label: l.name }))}
              onChange={(id) => {
                const name = preferredLanguages.find((l) => l.id === id)?.name ?? '';
                setDraft((d) => ({ ...d, rtiPreferredLanguageId: id, rtiPreferredLanguageName: name }));
                clearErr('rtiPreferredLanguageId');
              }}
            />
            <FieldError message={errors.rtiPreferredLanguageId} />
          </div>
        </div>

        <div className="ccr-fg" id="ag-rti-lifeLiberty" style={{ marginBottom: 14 }}>
          <label>{f.involvesLifeOrPersonalLiberty}</label>
          <div className="ccr-radio-row">
            <label className="ccr-radio">
              <input
                type="radio"
                name="agRtiLifeLiberty"
                checked={draft.rtiInvolvesLifeOrPersonalLiberty === 'yes'}
                onChange={() => setDraft((d) => ({ ...d, rtiInvolvesLifeOrPersonalLiberty: 'yes' }))}
              />
              <span>Yes</span>
            </label>
            <label className="ccr-radio">
              <input
                type="radio"
                name="agRtiLifeLiberty"
                checked={draft.rtiInvolvesLifeOrPersonalLiberty === 'no'}
                onChange={() => {
                  setDraft((d) => ({ ...d, rtiInvolvesLifeOrPersonalLiberty: 'no', rtiJustification: '' }));
                  clearErr('rtiJustification');
                }}
              />
              <span>No</span>
            </label>
          </div>
          <FieldError message={errors.rtiInvolvesLifeOrPersonalLiberty} />
        </div>

        {draft.rtiInvolvesLifeOrPersonalLiberty === 'yes' ? (
          <div className="ccr-fg" style={{ marginBottom: 14 }}>
            <label>{f.justification}</label>
            <textarea
              id="ag-rti-justification"
              className={inpClass(Boolean(errors.rtiJustification))}
              rows={3}
              value={draft.rtiJustification}
              onChange={(e) => {
                setDraft((d) => ({ ...d, rtiJustification: maxLen('rtiJustification', e.target.value, MAX_NAME) }));
              }}
              placeholder={agEnterPlaceholder('Justification')}
            />
            <FieldError message={errors.rtiJustification} />
          </div>
        ) : null}

        <div className="ccr-fg" style={{ marginBottom: 0 }}>
          <label>{f.additionalNotes}</label>
          <textarea
            className={inpClass(Boolean(errors.rtiAdditionalNotes))}
            rows={3}
            value={draft.rtiAdditionalNotes}
            onChange={(e) => setDraft((d) => ({ ...d, rtiAdditionalNotes: maxLen('rtiAdditionalNotes', e.target.value, MAX_NAME) }))}
            placeholder={f.additionalNotes}
          />
          <FieldError message={errors.rtiAdditionalNotes} />
        </div>
      </SectionCard>

      <AttorneyGeneralWizardActions
        draft={draft}
        setDraft={setDraft}
        currentStep={9}
        onBack={() => nav(attorneyGeneralWizardPath(8, draft.draftRequestId))}
        onNext={() => {
          if (validate()) nav(attorneyGeneralWizardPath(10, draft.draftRequestId));
        }}
        validateBeforeSave={validate}
      />
    </AttorneyGeneralWizardShell>
  );
}
